import "./hero-backdrop.css";

/**
 * Shared decorative backdrop for the modern-warm hero bands (about, pricing):
 * faint plotted-map linework (parcels, road curves, a dashed plotted route,
 * place dots in currentColor at low opacity) plus two dropped teardrop pins
 * (teal, then a floating coral). Extracted from the about hero so marketing
 * heroes share ONE implementation instead of drifting copies.
 *
 * The host section provides the band itself (position: relative, the
 * --gradient-hero background, clip-path seam, overflow: hidden) and lifts its
 * content above with z-index (var(--z-map-overlay)); this component only
 * paints the backdrop. Fully decorative: aria-hidden and pointer-events: none,
 * so it can never intercept clicks on hero controls.
 *
 * The auth band (src/app/(auth)/AuthShell.tsx) keeps its own taller variant of
 * this art (1440x820, tuned to a near-viewport band); folding it in would mean
 * a second path set or a visible change, so it stays separate on purpose.
 */

/** Teardrop map pin (same geometry as the landing hero / CTA-band pins). */
function HeroBackdropPin({ fill, rim, size = 32 }: { fill: string; rim?: string; size?: number }) {
  return (
    <svg width={size} height={size * 1.29} viewBox="0 0 34 44" fill="none" aria-hidden style={{ display: "block" }}>
      <path
        d="M17 42.5C17 42.5 3.5 24.8 3.5 15.2C3.5 7.7 9.5 1.8 17 1.8C24.5 1.8 30.5 7.7 30.5 15.2C30.5 24.8 17 42.5 17 42.5Z"
        style={{ fill, stroke: rim }}
        strokeWidth={rim ? 1.6 : 0}
      />
      <circle cx="17" cy="15.2" r="5" style={{ fill: "var(--map-marker-pin-dot)" }} />
    </svg>
  );
}

/**
 * Faint plotted-map linework behind the hero copy. preserveAspectRatio slice
 * lets the one 1440x560 drawing cover hero bands of different heights.
 */
function HeroBackdropMapArt() {
  return (
    <svg viewBox="0 0 1440 560" preserveAspectRatio="xMidYMid slice" aria-hidden className="pz-hero-backdrop__map">
      <g fill="currentColor" fillOpacity="0.05">
        <path d="M110 360 L258 340 L276 432 L126 454 Z" />
        <path d="M200 60 L332 46 L346 128 L212 144 Z" />
        <path d="M1080 80 L1214 66 L1230 150 L1094 166 Z" />
        <path d="M1120 380 L1258 364 L1274 456 L1134 474 Z" />
        <path d="M610 460 L770 448 L782 530 L620 544 Z" />
      </g>
      <g fill="none" stroke="currentColor" strokeLinecap="round">
        <path d="M-20 210 C 260 178, 560 240, 860 206 S 1280 160, 1460 182" strokeOpacity="0.1" strokeWidth="5" />
        <path d="M-20 390 C 300 370, 640 420, 960 390 S 1300 350, 1460 372" strokeOpacity="0.08" strokeWidth="3" />
        <path d="M260 -20 C 236 140, 280 340, 242 580" strokeOpacity="0.08" strokeWidth="3" />
        <path d="M1170 -20 C 1200 160, 1150 360, 1190 580" strokeOpacity="0.08" strokeWidth="3" />
        <path d="M-20 90 C 240 110, 520 70, 800 94 S 1220 122, 1460 92" strokeOpacity="0.06" strokeWidth="1.5" />
        <path d="M-20 490 C 280 474, 600 510, 920 486 S 1280 462, 1460 480" strokeOpacity="0.06" strokeWidth="1.5" />
        <path d="M660 -20 C 684 100, 640 240, 680 380" strokeOpacity="0.05" strokeWidth="1.5" />
      </g>
      <path
        d="M 120 130 C 310 320, 640 470, 940 400 S 1280 260, 1330 200"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="2.5"
        strokeDasharray="2 9"
        strokeLinecap="round"
      />
      <circle cx="300" cy="250" r="4" fill="currentColor" fillOpacity="0.35" />
      <circle cx="1110" cy="300" r="4" fill="currentColor" fillOpacity="0.35" />
      <circle cx="430" cy="440" r="3" fill="currentColor" fillOpacity="0.28" />
      <circle cx="1000" cy="120" r="3" fill="currentColor" fillOpacity="0.28" />
      <circle cx="750" cy="500" r="4" fill="currentColor" fillOpacity="0.3" />
    </svg>
  );
}

/** The full decorative scene: map linework + a dropped teal pin (left) and a floating coral pin (right). */
export function HeroBackdrop() {
  return (
    <div className="pz-hero-backdrop" aria-hidden>
      <HeroBackdropMapArt />
      <div className="pz-hero-backdrop__pin pz-hero-backdrop__pin--left">
        <div className="pz-hero-backdrop__drop">
          <HeroBackdropPin fill="var(--color-accent-default)" rim="var(--color-accent-hover)" size={30} />
        </div>
      </div>
      <div className="pz-hero-backdrop__pin pz-hero-backdrop__pin--right">
        <div className="pz-hero-backdrop__drop pz-hero-backdrop__drop--late">
          <div className="pz-hero-backdrop__float">
            <HeroBackdropPin fill="var(--color-secondary-default)" rim="var(--color-secondary-active)" size={44} />
          </div>
        </div>
      </div>
    </div>
  );
}
